import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Row, Col } from 'react-bootstrap';
import { apiRequests } from '../../shared/api/apiRequests';
import { toast } from 'react-toastify';
import { Availability } from '../../entities/Availability';
import { SEOMetatagsForm } from '../../shared/ui/SEOMetatagsForm';
import { CreateContent } from '../../features/CreateContent';
import { UploadPicture } from '../../features/UploadPicture';
import { Guid } from 'js-guid';
import { CONTENT_ITEM_KEYS } from '../../features/CreateContent/constants';
import { FormLayout, FormSection, FormField } from '../../shared/ui/FormLayout';

const CreateInvestProject = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mainImage, setMainImage] = useState(null);
  const [presentation, setPresentation] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    beforeBlocksTitle: '',
    block1Header: '',
    block1Text: '',
    block2Header: '',
    block2Text: '',
    block3Header: '',
    block3Text: '',
    content: {
      items: [],
    },
    availability: {
      availableInAllCountries: true,
      allowedCountriesIds: [],
      disallowedCountriesIds: [],
    },
    seoMetatags: {
      title: null,
      description: null,
      keywords: null,
    },
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Отладка
    console.log('Form submit:', {
      title: formData.title,
      description: formData.description,
      mainImage: mainImage,
      mainImageType: typeof mainImage,
      mainImageName: mainImage?.name
    });

    // Валидация
    if (!formData.title.trim()) {
      toast.error('Введите название проекта');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Введите описание проекта');
      return;
    }
    if (!mainImage) {
      toast.error('Загрузите главное изображение');
      console.log('mainImage is:', mainImage);
      return;
    }

    setLoading(true);

    try {
      const body = new FormData();
      
      // Главное изображение (обязательно)
      body.append('mainImg', mainImage, mainImage.name || 'image.jpg');
      
      // Презентация (опционально)
      if (presentation) {
        body.append('presentation', presentation, presentation.name);
      }

      // Обработка Content Items
      const bodyItems = formData.content.items.map((i) => {
        switch (i.discriminator) {
          case 'TextContentItem':
            return i;
          case 'ImageSliderContentItem':
            const items = i.images.map((z) => {
              const guid = Guid.newGuid().toString();
              body.append(guid, z.imgPath);
              return { imgPath: '', guid, discriminator: 'ImageContentItem' };
            });
            return { ...i, images: items };
          default:
            body.append(i.guid, i[CONTENT_ITEM_KEYS[i.discriminator]]);
            return { ...i, [CONTENT_ITEM_KEYS[i.discriminator]]: '' };
        }
      });

      const bodyModel = JSON.parse(JSON.stringify(formData));
      bodyModel.content.items = bodyItems;

      body.append('model', JSON.stringify(bodyModel));

      await apiRequests.investProjects.create(body);
      
      toast.success('Инвест проект создан');
      navigate('/invest-projects');
    } catch (error) {
      console.error('Error creating invest project:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Ошибка при создании проекта';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormLayout
      onSubmit={handleSubmit}
      onBack={() => navigate('/invest-projects')}
      saving={loading}
      submitText="Создать проект"
      savingText="Создание..."
    >
      <FormSection title="Основная информация">
        <Row>
          <Col lg={8}>
            <FormField label="Название проекта" required>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Введите название проекта"
              />
            </FormField>

            <FormField label="Описание" required>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Введите описание проекта"
              />
            </FormField>

            <FormField label="Презентация" hint={presentation ? `Выбран: ${presentation.name}` : 'Опционально'}>
              <Form.Control
                type="file"
                accept=".pdf,.ppt,.pptx,.doc,.docx"
                onChange={(e) => setPresentation(e.target.files[0])}
              />
            </FormField>
          </Col>

          <Col lg={4}>
            <FormField label="Главное изображение" required>
              <UploadPicture
                onSelect={setMainImage}
                entityType="portfolio"
              />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Блоки информации">
        <FormField label="Заголовок перед блоками">
          <Form.Control
            type="text"
            name="beforeBlocksTitle"
            value={formData.beforeBlocksTitle}
            onChange={handleChange}
            placeholder="Введите заголовок"
          />
        </FormField>

        <Row>
          <Col md={4}>
            <h6 className="mt-3 mb-2">Блок 1</h6>
            <FormField label="Заголовок">
              <Form.Control
                type="text"
                name="block1Header"
                value={formData.block1Header}
                onChange={handleChange}
              />
            </FormField>
            <FormField label="Текст">
              <Form.Control
                as="textarea"
                rows={2}
                name="block1Text"
                value={formData.block1Text}
                onChange={handleChange}
              />
            </FormField>
          </Col>
          <Col md={4}>
            <h6 className="mt-3 mb-2">Блок 2</h6>
            <FormField label="Заголовок">
              <Form.Control
                type="text"
                name="block2Header"
                value={formData.block2Header}
                onChange={handleChange}
              />
            </FormField>
            <FormField label="Текст">
              <Form.Control
                as="textarea"
                rows={2}
                name="block2Text"
                value={formData.block2Text}
                onChange={handleChange}
              />
            </FormField>
          </Col>
          <Col md={4}>
            <h6 className="mt-3 mb-2">Блок 3</h6>
            <FormField label="Заголовок">
              <Form.Control
                type="text"
                name="block3Header"
                value={formData.block3Header}
                onChange={handleChange}
              />
            </FormField>
            <FormField label="Текст">
              <Form.Control
                as="textarea"
                rows={2}
                name="block3Text"
                value={formData.block3Text}
                onChange={handleChange}
              />
            </FormField>
          </Col>
        </Row>
      </FormSection>

      <FormSection title="Контент">
        <CreateContent
          form={formData}
          setForm={setFormData}
        />
      </FormSection>

      <FormSection title="Доступность">
        <Availability
          onSelect={(val) =>
            setFormData((prev) => ({
              ...prev,
              availability: val,
            }))
          }
          value={formData.availability}
        />
      </FormSection>

      <FormSection title="SEO настройки">
        <SEOMetatagsForm
          seoMetatags={formData.seoMetatags}
          onChange={(updatedMetatags) =>
            setFormData((prev) => ({
              ...prev,
              seoMetatags: updatedMetatags,
            }))
          }
        />
      </FormSection>
    </FormLayout>
  );
};

export default CreateInvestProject;
